import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from "recharts";

const COLORS = { gpt: "#111111", claude: "#FF3B30", gemini: "#002FA7" };
const LABELS = { gpt: "GPT-5.2", claude: "Claude 4.5", gemini: "Gemini 3 Pro" };

export default function EquityChart({ snapshots }) {
  const data = useMemo(() => {
    if (!snapshots) return [];
    const timestamps = new Set();
    Object.values(snapshots).forEach((series) =>
      series.forEach((p) => timestamps.add(p.timestamp))
    );
    const sorted = [...timestamps].sort();
    return sorted.map((ts) => {
      const point = { timestamp: ts, label: new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit" }) };
      for (const id of ["gpt", "claude", "gemini"]) {
        const series = snapshots[id] || [];
        // Pick latest snapshot at this exact ts; otherwise carry forward
        const exact = series.find((p) => p.timestamp === ts);
        if (exact) point[id] = exact.total_value;
        else {
          const prior = [...series].reverse().find((p) => p.timestamp < ts);
          if (prior) point[id] = prior.total_value;
        }
      }
      return point;
    });
  }, [snapshots]);

  return (
    <section data-testid="equity-chart" className="border border-arena-line bg-white">
      <div className="px-6 lg:px-8 py-4 border-b border-arena-line flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl tracking-tighter">EQUITY CURVE</h2>
        <div className="flex items-center gap-5 text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
          {["gpt", "claude", "gemini"].map((id) => (
            <div key={id} className="flex items-center gap-2">
              <span className="w-3 h-[2px]" style={{ background: COLORS[id] }} />
              {LABELS[id]}
            </div>
          ))}
        </div>
      </div>
      <div className="p-2 sm:p-6 lg:p-8" style={{ height: 380 }}>
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-arena-soft text-sm font-mono">
            Awaiting first round…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E5E5E5" />
              <XAxis
                dataKey="label"
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#737373" }}
                axisLine={{ stroke: "#E5E5E5" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#737373" }}
                axisLine={{ stroke: "#E5E5E5" }}
                tickLine={false}
                domain={["auto", "auto"]}
                tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid #111",
                  borderRadius: 0,
                  fontFamily: "IBM Plex Mono",
                  fontSize: 12,
                }}
                labelStyle={{ color: "#737373", fontSize: 10, textTransform: "uppercase", letterSpacing: 2 }}
                formatter={(value, name) => [`$${Number(value).toFixed(2)}`, LABELS[name] || name]}
              />
              <ReferenceLine y={10000} stroke="#A3A3A3" strokeDasharray="4 4" label={{ value: "$10,000 START", position: "insideTopRight", fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#737373" }} />
              <Line type="monotone" dataKey="gpt" stroke={COLORS.gpt} strokeWidth={2} dot={false} animationDuration={800} />
              <Line type="monotone" dataKey="claude" stroke={COLORS.claude} strokeWidth={2} dot={false} animationDuration={800} />
              <Line type="monotone" dataKey="gemini" stroke={COLORS.gemini} strokeWidth={2} dot={false} animationDuration={800} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
